import { Section, CodeBlock, InfoBox } from "@/components"

export function ReactTailwindComponentsPage() {
  return (
    <div className="space-y-10">
      <div className="space-y-4">
        <h1 className="text-4xl font-bold">Components</h1>
        <p className="text-lg text-muted-foreground max-w-4xl leading-relaxed">
          Explore the custom UI components built with Tailwind CSS. These components provide 
          consistent styling and reusable patterns throughout your application.
        </p>
      </div>

      <Section title="Component Organization" description="How components are structured">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="bg-card border rounded-lg p-6 space-y-3">
            <h3 className="font-semibold">common/</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Custom reusable components built with Tailwind CSS utilities for common UI patterns.
            </p>
          </div>
          <div className="bg-card border rounded-lg p-6 space-y-3">
            <h3 className="font-semibold">layout/</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Layout-specific components like Sidebar, Header, and Navigation with responsive design.
            </p>
          </div>
        </div>
      </Section>

      <Section title="Common Components" description="Pre-built reusable components">
        <div className="space-y-8">
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">StatCard</h3>
            <p className="text-muted-foreground">
              Display statistics and metrics with optional trend indicators and icons.
            </p>
            <CodeBlock
              code={`interface StatCardProps {
  title: string
  value: string | number
  icon?: React.ReactNode
  trend?: {
    value: number
    isPositive: boolean
  }
  description?: string
}

export function StatCard({ 
  title, 
  value, 
  icon, 
  trend, 
  description 
}: StatCardProps) {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-gray-600 dark:text-gray-400">
          {title}
        </h3>
        {icon && (
          <div className="text-blue-600 dark:text-blue-400">
            {icon}
          </div>
        )}
      </div>
      <p className="text-3xl font-bold text-gray-900 dark:text-white">
        {value}
      </p>
      {trend && (
        <div className="flex items-center mt-2 text-sm">
          <span className={\`font-medium \${
            trend.isPositive ? "text-green-600" : "text-red-600"
          }\`}>
            {trend.isPositive ? "+" : ""}{trend.value}%
          </span>
          <span className="text-gray-600 dark:text-gray-400 ml-2">
            vs last month
          </span>
        </div>
      )}
      {description && (
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
          {description}
        </p>
      )}
    </div>
  )
}`}
              language="typescript"
              filename="src/components/common/StatCard.tsx"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold">LoadingSpinner</h3>
            <p className="text-muted-foreground">
              Show loading states with customizable size and optional text.
            </p>
            <CodeBlock
              code={`interface LoadingSpinnerProps {
  size?: "sm" | "md" | "lg"
  text?: string
}

export function LoadingSpinner({ size = "md", text }: LoadingSpinnerProps) {
  const sizeClasses = {
    sm: "h-4 w-4",
    md: "h-8 w-8",
    lg: "h-12 w-12"
  }

  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className={\`animate-spin rounded-full border-4 border-gray-200 border-t-blue-600 \${sizeClasses[size]}\`} />
      {text && (
        <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">
          {text}
        </p>
      )}
    </div>
  )
}`}
              language="typescript"
              filename="src/components/common/LoadingSpinner.tsx"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold">ErrorMessage</h3>
            <p className="text-muted-foreground">
              Display error states with optional retry functionality.
            </p>
            <CodeBlock
              code={`interface ErrorMessageProps {
  message: string
  onRetry?: () => void
}

export function ErrorMessage({ message, onRetry }: ErrorMessageProps) {
  return (
    <div className="flex flex-col items-center justify-center py-12">
      <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-6 max-w-md">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">
              Error
            </h3>
            <p className="mt-1 text-sm text-red-700 dark:text-red-300">
              {message}
            </p>
            {onRetry && (
              <button
                onClick={onRetry}
                className="mt-4 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium"
              >
                Try Again
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}`}
              language="typescript"
              filename="src/components/common/ErrorMessage.tsx"
            />
          </div>

          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Button</h3>
            <p className="text-muted-foreground">
              Flexible button component with multiple variants and sizes.
            </p>
            <CodeBlock
              code={`interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "danger" | "ghost"
  size?: "sm" | "md" | "lg"
  children: React.ReactNode
}

export function Button({ 
  variant = "primary", 
  size = "md", 
  children, 
  className = "",
  ...props 
}: ButtonProps) {
  const baseClasses = "inline-flex items-center justify-center font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2"
  
  const variantClasses = {
    primary: "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500",
    secondary: "bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500 dark:bg-gray-700 dark:text-white dark:hover:bg-gray-600",
    danger: "bg-red-600 text-white hover:bg-red-700 focus:ring-red-500",
    ghost: "text-gray-700 hover:bg-gray-100 focus:ring-gray-500 dark:text-gray-300 dark:hover:bg-gray-800"
  }
  
  const sizeClasses = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-base",
    lg: "px-6 py-3 text-lg"
  }

  return (
    <button
      className={\`\${baseClasses} \${variantClasses[variant]} \${sizeClasses[size]} \${className}\`}
      {...props}
    >
      {children}
    </button>
  )
}`}
              language="typescript"
              filename="src/components/common/Button.tsx"
            />
          </div>
        </div>
      </Section>

      <Section title="Layout Components" description="Navigation and structure">
        <div className="space-y-8">
          <div className="space-y-4">
            <h3 className="text-xl font-semibold">Sidebar</h3>
            <p className="text-muted-foreground">
              Responsive sidebar navigation with active state and icons.
            </p>
            <CodeBlock
              code={`import { Link, useLocation } from "react-router-dom"
import { useUIStore } from "@/store/ui.store"

interface MenuItem {
  icon: React.ReactNode
  label: string
  path: string
}

interface SidebarProps {
  items: MenuItem[]
}

export function Sidebar({ items }: SidebarProps) {
  const location = useLocation()
  const { sidebarCollapsed } = useUIStore()

  return (
    <aside className={\`
      bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700
      transition-all duration-300
      \${sidebarCollapsed ? "w-16" : "w-64"}
    \`}>
      <div className="flex flex-col h-full">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-xl font-bold text-gray-900 dark:text-white">
            {sidebarCollapsed ? "A" : "App Name"}
          </h1>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          {items.map((item) => {
            const isActive = location.pathname === item.path
            
            return (
              <Link
                key={item.path}
                to={item.path}
                className={\`
                  flex items-center gap-3 px-3 py-2 rounded-lg
                  transition-colors
                  \${isActive 
                    ? "bg-blue-600 text-white" 
                    : "text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  }
                \`}
              >
                <span className="flex-shrink-0">{item.icon}</span>
                {!sidebarCollapsed && (
                  <span className="font-medium">{item.label}</span>
                )}
              </Link>
            )
          })}
        </nav>
      </div>
    </aside>
  )
}`}
              language="typescript"
              filename="src/components/layout/Sidebar.tsx"
            />
          </div>
        </div>
      </Section>

      <Section title="Building Custom Components" description="Create your own">
        <div className="space-y-6">
          <p className="text-muted-foreground leading-relaxed">
            Creating custom components with Tailwind CSS is straightforward. Use utility classes 
            to style your components and extract common patterns.
          </p>
          
          <div className="space-y-4">
            <h3 className="text-lg font-semibold">Example: Card Component</h3>
            <CodeBlock
              code={`interface CardProps {
  children: React.ReactNode
  className?: string
  padding?: "sm" | "md" | "lg"
}

export function Card({ children, className = "", padding = "md" }: CardProps) {
  const paddingClasses = {
    sm: "p-4",
    md: "p-6",
    lg: "p-8"
  }

  return (
    <div className={\`
      bg-white dark:bg-gray-800 
      border border-gray-200 dark:border-gray-700 
      rounded-lg 
      \${paddingClasses[padding]}
      \${className}
    \`}>
      {children}
    </div>
  )
}

// Usage
<Card padding="lg">
  <h2 className="text-2xl font-bold mb-4">Card Title</h2>
  <p className="text-gray-600 dark:text-gray-400">
    Card content goes here
  </p>
</Card>`}
              language="typescript"
            />
          </div>
        </div>
      </Section>

      <div className="bg-primary/5 border border-primary/20 rounded-xl p-8 space-y-4">
        <h2 className="text-2xl font-bold">Component Best Practices</h2>
        <ul className="space-y-3 text-sm">
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use Tailwind's utility classes for consistent styling</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Extract repeated patterns into reusable components</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Use TypeScript interfaces to define component props clearly</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Support dark mode with dark: variants</span>
          </li>
          <li className="flex gap-3">
            <span className="text-primary shrink-0">→</span>
            <span>Make components responsive with Tailwind's breakpoint prefixes</span>
          </li>
        </ul>
      </div>
    </div>
  )
}
